import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

// Customer interface
export interface Customer {
  id: string;
  avatar?: string;
  firstName: string;
  lastName: string;
  email: string;
  birthday?: Date | string;
  firstSeen?: Date | string;
  lastSeen?: Date | string;
  hasOrdered?: boolean;
  latestPurchase?: Date | string;
  hasNewsletter?: boolean;
  groups?: string[];
  nbCommands?: number;
  totalSpent?: number;
  address?: string;
  city?: string;
  stateAbbr?: string;
  zipcode?: string;
  phone?: string;
}

export interface CustomerListResponse {
  success: boolean;
  data: Customer[];
  total?: number;
}

export interface CustomerResponse {
  success: boolean;
  data: Customer;
  message?: string;
}

class CustomersService {
  private apiRoot = configs.apiRoot;

  async getCustomers(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    q?: string;
  }): Promise<Customer[] & { __contentRange?: string; __total?: number }> {
    const queryParams = new URLSearchParams();

    const page = params?.page ?? 1;
    let size = 20; // Default page size for pagination

    if (params?.size) {
      size = params.size;
    } else if (params?.perPage) {
      size = params.perPage;
    }

    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());

    if (params?.q) queryParams.append('q', params.q);
    if (params?.filter)
      queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/customers${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch customers: ${response.statusText}`
      );
    }

    const result = await response.json();

    // API returns array directly (via sendFormattedResponse), not wrapped in { data: ... }
    // Also check Content-Range header for total count
    const contentRange = response.headers.get('Content-Range');
    let total = 0;

    if (contentRange) {
      // Content-Range format: "customers 1-20/100"
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    // Handle both array response and object with data property
    const customers = Array.isArray(result) ? result : result.data || [];

    // If total not found in header, try result.total or use array length
    if (!total) {
      total = result.total || customers.length;
    }

    // Add metadata
    (customers as any).__total = total;
    (customers as any).__contentRange =
      contentRange ||
      `customers ${(page - 1) * size + 1}-${Math.min(
        page * size,
        total
      )}/${total}`;

    console.log(`📊 Customers API Response:`, {
      isArray: Array.isArray(result),
      customersCount: customers.length,
      total,
      contentRange,
      resultType: typeof result,
      page,
      size,
      url: url.split('?')[0], // Log without query params for security
      sampleCustomer: customers[0]
        ? {
            id: customers[0].id,
            firstName: customers[0].firstName,
            lastName: customers[0].lastName,
            name: (customers[0] as any).name,
            email: customers[0].email,
          }
        : null,
    });

    return customers;
  }

  async getCustomer(id: string): Promise<Customer> {
    const url = `${this.apiRoot}/customers/${id}`;
    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch customer: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async createCustomer(data: Partial<Customer>): Promise<Customer> {
    const url = `${this.apiRoot}/customers`;
    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to create customer: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async updateCustomer(id: string, data: Partial<Customer>): Promise<Customer> {
    const url = `${this.apiRoot}/customers/${id}`;
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to update customer: ${response.statusText}`
      );
    }

    const result = await response.json();
    return result.data || result;
  }

  async deleteCustomer(id: string): Promise<void> {
    const url = `${this.apiRoot}/customers/${id}`;
    const response = await authenticatedFetch(url, { method: 'DELETE' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to delete customer: ${response.statusText}`
      );
    }
  }
}

export const customersService = new CustomersService();
